import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddOnItem } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import ConnectGmailDialog from "components/ConnectGmailDialog";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

interface RepitchingInfo {
  followups: number | undefined;
  review: boolean | undefined;
}

const IntakeForm = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { currentOrg, userIntegrations } = useContext(OrganizationUserContext);
  const { emailHealth, fetchIntegrationHealth, userIntegration, scopes } =
    useContext(UserIntegrationsContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);

  const [activeStep, setActiveStep] = useState(0);
  const [userIntegrationDialogState, setUserIntegrationDialogState] =
    useState(false);

  const [settings, setSettings] = useState<RepitchingInfo>({
    followups: undefined,
    review: undefined,
  });
  const [repitchingData, setRepitchingData] = useState<AddOnItem | null>(null);
  const [loading, setLoading] = useState(false);

  const connectToGmail = () => {
    trackEvent("Repitching - Connect to Gmail Button Clicked");
    if (emailHealth) {
      setActiveStep(1);
    } else {
      setUserIntegrationDialogState(true);
    }
  };

  const onEmailConnected = async () => {
    setUserIntegrationDialogState(false);
    setActiveStep(1);
  };

  const goToDashboard = () => {
    navigate(`/${routes.home}`);
  };

  const handleChange = (e: CustomEvent) => {
    const { name, value } = e.target;
    setSettings((prev: RepitchingInfo) => {
      const copy = makeDeepCopy(prev);
      copy[name] = value;
      return copy;
    });
  };

  const fetchRepitchingItems = async () => {
    try {
      const { addOnItems } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/repitching-items`,
        "GET",
      );
      if (addOnItems?.length > 0) {
        setRepitchingData(addOnItems?.[0]);
        setSettings(addOnItems?.[0]?.purchaseDetails);
      }
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const storeSettings = async () => {
    if (!repitchingData) return;
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/${repitchingData.id}/purchase-details`,
        "PATCH",
        {},
        settings,
      );
      setAlert(
        "Successfully updated. Please check your inbox for an email confirmation",
        "success",
      );
      setActiveStep(2);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      label: "Connect your Gmail account",
      description: (
        <Grid item xs={12}>
          <Typography>
            Providing access to your Gmail account helps us identify your past
            partnerships and craft your personalized outreach.
          </Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={connectToGmail}
            size="small"
            disableElevation
            sx={{ mr: 1, mt: 1 }}
          >
            Connect
          </Button>
        </Grid>
      ),
    },
    {
      label: "Customizations",
      description: (
        <Grid item xs={12}>
          <Typography gutterBottom>
            Select the number of follow ups you'd like sent after the initial
            e-mail. Each followup will be sent every 4 business days.
          </Typography>
          <FormControl fullWidth>
            <Select
              value={settings.followups || ""}
              name="followups"
              onChange={handleChange}
              variant="standard"
              sx={{ width: 200 }}
              displayEmpty
            >
              <MenuItem value={""}>
                <em>Select</em>
              </MenuItem>
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ mt: 3 }} gutterBottom>
            Do you want to review the emails before we send them out?
          </Typography>
          <Select
            value={settings.review || ""}
            name="review"
            variant="standard"
            sx={{ width: 200 }}
            displayEmpty
            onChange={handleChange}
          >
            <MenuItem value={""}>
              <em>Select</em>
            </MenuItem>
            <MenuItem value={"true"}>Yes</MenuItem>
            <MenuItem value={"false"}>No</MenuItem>
          </Select>

          <Grid item xs={12}>
            <LoadingButton
              disableElevation
              variant="contained"
              sx={{ mt: 2 }}
              onClick={storeSettings}
              disabled={
                settings.review === undefined ||
                settings.followups === undefined
              }
              loading={loading}
            >
              Next
            </LoadingButton>
          </Grid>
        </Grid>
      ),
    },
  ];

  const getActiveStep = () => {
    if (settings?.review !== undefined && settings.followups) {
      setActiveStep(2);
    } else if (emailHealth) {
      setActiveStep(1);
    } else {
      setActiveStep(0);
    }
  };

  useEffect(() => {
    getActiveStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailHealth]);

  useEffect(() => {
    if (currentOrg?.id) {
      fetchIntegrationHealth(currentOrg.id);
      fetchRepitchingItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <>
      <Grid container item xs={12} justifyContent={"center"} direction="column">
        <Typography variant="h3" textAlign="center" sx={{ mb: 3 }}>
          {activeStep === 2
            ? "All steps completed! 💯"
            : "Complete these steps to start"}
        </Typography>

        <Grid container justifyContent="center" sx={{ px: { xs: 2, md: 6 } }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Box sx={{ fontSize: 20 }}>
                    Step {index + 1} - {step.label}
                  </Box>
                </StepLabel>
                <StepContent>{step.description}</StepContent>
              </Step>
            ))}
          </Stepper>
        </Grid>

        {activeStep === 2 && (
          <>
            <Typography textAlign={"center"} sx={{ mt: 3 }}>
              You will receive an email confirmation in your inbox shortly.
            </Typography>

            <Grid container justifyContent={"center"}>
              <Button
                sx={{ mt: 3, width: 300 }}
                variant="outlined"
                onClick={goToDashboard}
              >
                Go To Dashboard{" "}
                <Box
                  component="i"
                  className="fa-regular fa-arrow-right"
                  sx={{ ml: 1 }}
                />
              </Button>
            </Grid>
          </>
        )}

        {/* Create a custom gmail connect in order to perform action automatically
         after connecting */}
        <ConnectGmailDialog
          openIntegrationDialog={userIntegrationDialogState}
          setOpenIntegrationDialog={(newOpen: boolean) =>
            setUserIntegrationDialogState(newOpen)
          }
          userIntegrations={userIntegrations}
          userIntegration={userIntegration}
          scopes={scopes}
          emailHealth={emailHealth}
          onSuccessAction={onEmailConnected}
          title="Connect your Gmail account"
        />
      </Grid>
    </>
  );
};

export default IntakeForm;
